import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Page-local pending-correction batch state.
 *
 * Holds a Map of original_transaction_id → reason_id chosen on the
 * detail page. Installs a `beforeunload` warning so the operator can't
 * accidentally lose a half-built batch by closing the tab or hitting
 * refresh.
 *
 * React Router's `useBlocker` would also catch in-app navigation, but
 * it only works with a Data Router (`createBrowserRouter`) and this app
 * uses `<BrowserRouter>`. Falling back to `beforeunload` only — the
 * "Clear batch" header button gives the operator a deliberate way to
 * walk away inside the app, which is the most common path anyway.
 *
 * The batch is intentionally not persisted to localStorage — leaving the
 * page is meant to be a clean reset (operator should commit or
 * explicitly clear).
 */
export interface PendingBatch {
  pending: Map<number, number>;
  size: number;
  toggle: (originalTransactionId: number, reasonId: number | null) => void;
  setReason: (originalTransactionId: number, reasonId: number) => void;
  remove: (originalTransactionId: number) => void;
  clear: () => void;
}

export function usePendingBatch(): PendingBatch {
  const [pending, setPending] = useState<Map<number, number>>(new Map());

  const setReason = useCallback((tx: number, rid: number) => {
    setPending((m) => {
      const next = new Map(m);
      next.set(tx, rid);
      return next;
    });
  }, []);

  const remove = useCallback((tx: number) => {
    setPending((m) => {
      if (!m.has(tx)) return m;
      const next = new Map(m);
      next.delete(tx);
      return next;
    });
  }, []);

  const toggle = useCallback(
    (tx: number, rid: number | null) => {
      if (rid === null) remove(tx);
      else setReason(tx, rid);
    },
    [remove, setReason],
  );

  const clear = useCallback(() => setPending(new Map()), []);

  // beforeunload: native window-close / refresh / OS-back guard.
  useEffect(() => {
    if (pending.size === 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome ignores custom strings; we still set returnValue so the
      // browser shows its generic confirm dialog.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [pending.size]);

  return useMemo(
    () => ({
      pending,
      size: pending.size,
      toggle,
      setReason,
      remove,
      clear,
    }),
    [pending, toggle, setReason, remove, clear],
  );
}
